import React, { useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import EditIcon from "@mui/icons-material/Edit"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"

import { WheelPrizeEditor } from "./WheelPrizeEditor"
import { WHEEL_PRIZE_TYPE_LABELS, WheelPrizeFormData } from "./wheelPrizeTypes"
import { DOMINANT_ODDS_THRESHOLD_PERCENT, calculateOddsPercent } from "./wheelPrizeValidation"

const createPrizeId = () => `prize-${Date.now()}-${Math.floor(Math.random() * 100_000)}`

export const WheelPrizeManager: React.FC<{
  prizes: WheelPrizeFormData[]
  onChange: (prizes: WheelPrizeFormData[]) => void
}> = ({ prizes, onChange }) => {
  const [editingPrize, setEditingPrize] = useState<WheelPrizeFormData | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)

  const odds = calculateOddsPercent(prizes)
  const sorted = [...prizes].sort((a, b) => a.sortOrder - b.sortOrder)

  const persist = (next: WheelPrizeFormData[]) =>
    onChange(next.map((prize, index) => ({ ...prize, sortOrder: index })))

  const handleDragEnd = (result: { destination: { index: number } | null; source: { index: number } }) => {
    if (!result.destination) return
    const next = Array.from(sorted)
    const [moved] = next.splice(result.source.index, 1)
    next.splice(result.destination.index, 0, moved)
    persist(next)
  }

  const openAdd = () => {
    setEditingPrize(null)
    setEditorOpen(true)
  }

  const openEdit = (prize: WheelPrizeFormData) => {
    setEditingPrize(prize)
    setEditorOpen(true)
  }

  const handleSavePrize = (prize: WheelPrizeFormData) => {
    const exists = prizes.some((existing) => existing.id === prize.id)
    persist(exists ? sorted.map((existing) => (existing.id === prize.id ? prize : existing)) : [...sorted, prize])
    setEditorOpen(false)
  }

  const handleDuplicate = (prize: WheelPrizeFormData) => {
    persist([...sorted, { ...prize, id: createPrizeId(), label: `${prize.label} (copy)` }])
  }

  const handleDelete = (prizeId: string) => {
    persist(sorted.filter((prize) => prize.id !== prizeId))
  }

  const handleToggleEnabled = (prizeId: string) => {
    persist(sorted.map((prize) => (prize.id === prizeId ? { ...prize, enabled: !prize.enabled } : prize)))
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Prizes</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openAdd}>
          Add prize
        </Button>
      </Stack>

      {sorted.length === 0 && <Alert severity="warning">No prizes configured — the wheel has nothing to award.</Alert>}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="wheel-prizes">
          {(provided) => (
            <Stack spacing={1.5} ref={provided.innerRef} {...provided.droppableProps}>
              {sorted.map((prize, index) => {
                const oddsPercent = odds.get(prize.id) ?? 0
                const isDominant = prize.enabled && oddsPercent >= DOMINANT_ODDS_THRESHOLD_PERCENT
                return (
                  <Draggable key={prize.id} draggableId={prize.id} index={index}>
                    {(dragProvided) => (
                      <Card
                        variant="outlined"
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        sx={{ opacity: prize.enabled ? 1 : 0.55 }}
                      >
                        <CardContent>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box {...dragProvided.dragHandleProps} sx={{ display: "flex", cursor: "grab" }}>
                              <DragIndicatorIcon color="disabled" />
                            </Box>

                            {prize.imageUrl ? (
                              <Box
                                component="img"
                                src={prize.imageUrl}
                                alt=""
                                sx={{ width: 48, height: 48, objectFit: "contain", borderRadius: 1 }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 1,
                                  bgcolor: "action.hover",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  {WHEEL_PRIZE_TYPE_LABELS[prize.type].slice(0, 1)}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography fontWeight={600}>{prize.label || "(untitled prize)"}</Typography>
                                <Chip size="small" label={WHEEL_PRIZE_TYPE_LABELS[prize.type]} />
                                {prize.featured && <Chip size="small" color="secondary" label="Featured" />}
                                {prize.grandPrize && <Chip size="small" color="warning" label="Grand prize" />}
                                {!prize.winning && <Chip size="small" variant="outlined" label="Try Again (no win)" />}
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                Weight {prize.weight} • ≈ {oddsPercent.toFixed(1)}% odds
                                {(prize.totalQuantity || prize.quantityPerDay || prize.perUserLimit) && " • capped inventory"}
                              </Typography>
                              {isDominant && (
                                <Typography variant="caption" color="warning.main">
                                  This prize dominates the odds — consider lowering its weight.
                                </Typography>
                              )}
                            </Box>

                            <Switch checked={prize.enabled} onChange={() => handleToggleEnabled(prize.id)} />
                            <Tooltip title="Edit">
                              <IconButton onClick={() => openEdit(prize)} aria-label={`Edit ${prize.label}`}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicate">
                              <IconButton onClick={() => handleDuplicate(prize)} aria-label={`Duplicate ${prize.label}`}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => handleDelete(prize.id)} aria-label={`Delete ${prize.label}`}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      <WheelPrizeEditor
        open={editorOpen}
        initialPrize={editingPrize}
        nextSortOrder={sorted.length}
        onClose={() => setEditorOpen(false)}
        onSave={handleSavePrize}
      />
    </Box>
  )
}

export default WheelPrizeManager
